import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { updateMetaTags, defaultMeta } from '../utils/metaTags';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalculator, FiClock, FiBookOpen, FiGrid, FiArrowRight } = FiIcons;

const HowToUse = () => {
  useEffect(() => {
    updateMetaTags({
      title: 'How to Use UK Salary Toolkit | Step-by-Step Calculator Guide',
      description: 'Learn how to use our UK tax calculators. Step-by-step guide to calculating take-home pay, hourly rates, student loan repayments, and salary comparisons for 2025/26.',
      keywords: 'how to use UK salary calculator, tax calculator guide, take-home pay guide, UK tax tools',
      canonical: `${defaultMeta.siteUrl}/how-to-use`,
    });
  }, []);

  const tools = [
    {
      icon: FiCalculator,
      title: 'Take-Home Pay Calculator',
      path: '/',
      description: 'Calculate your exact net pay after Income Tax, National Insurance, pension, and student loan deductions. Enter your salary, region, and any deductions to see monthly, weekly, and annual take-home pay.',
      whoFor: 'Anyone earning a UK salary who wants to know their actual take-home pay.',
    },
    {
      icon: FiClock,
      title: 'Salary to Hourly Converter',
      path: '/salary-to-hourly',
      description: 'Convert between annual salary and hourly rate (and vice versa). Essential for comparing job offers or understanding part-time vs full-time pay. Uses UK standard 37.5 hours per week (customizable).',
      whoFor: 'Job seekers, contractors, and anyone comparing pay structures.',
    },
    {
      icon: FiBookOpen,
      title: 'Student Loan Estimator',
      path: '/student-loan-estimator',
      description: 'Estimate your monthly student loan repayments based on your salary and loan plan (Plan 1, 2, 4, 5, or Postgraduate). Shows exactly when repayments kick in and how much you\'ll pay.',
      whoFor: 'UK graduates with student loans planning their finances.',
    },
    {
      icon: FiGrid,
      title: 'Take-Home Pay Table',
      path: '/take-home-table',
      description: 'Compare monthly take-home pay across salary levels from £20k to £200k. Switch between England/Wales/NI and Scotland to see regional tax differences. Perfect for salary negotiations or budgeting.',
      whoFor: 'Anyone comparing salary levels or planning career progression.',
    },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-10">
      <motion.section 
        className="text-center space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
          How to Use UK Salary Toolkit
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          A step-by-step guide to our free UK tax calculators. Find out what each tool does, who it's for, and how to interpret your results.
        </p>
      </motion.section>

      <motion.section 
        className="space-y-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">What We Offer</h2>
          <p className="text-gray-700 leading-relaxed">
            UK Salary Toolkit provides free, accurate UK tax calculators for the 2025/26 tax year. All calculations run in your browser – we never see or store your salary data. Our tools use official HMRC rates and cover England, Wales, Scotland, and Northern Ireland.
          </p>
        </div>

        {tools.map((tool, index) => (
          <motion.article
            key={tool.path}
            className="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-gray-100 hover:border-blue-200 transition-colors"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.05 }}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <SafeIcon icon={tool.icon} className="text-blue-600" size={24} />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{tool.title}</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">{tool.description}</p>
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-sm font-medium text-gray-600">Who it's for: {tool.whoFor}</p>
                </div>
                <Link
                  to={tool.path}
                  className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700"
                >
                  Use {tool.title}
                  <SafeIcon icon={FiArrowRight} size={18} />
                </Link>
              </div>
            </div>
          </motion.article>
        ))}

        <div className="bg-amber-50 rounded-xl p-6 border border-amber-100">
          <h3 className="font-semibold text-gray-900 mb-3">How to Interpret Results</h3>
          <ul className="space-y-2 text-gray-700 text-sm leading-relaxed">
            <li>• <strong>Take-home pay</strong> is your net pay after tax and National Insurance. It does not include pension or student loan deductions unless you enter them.</li>
            <li>• <strong>Scotland</strong> has different income tax rates and bands – always select your region for accurate results.</li>
            <li>• <strong>Student loan repayments</strong> are based on your plan and threshold. Repayments stop if your salary drops below the threshold.</li>
            <li>• Our calculators are for <strong>guidance only</strong> and do not constitute financial advice. For official figures, use <a href="https://www.gov.uk/estimate-income-tax" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">GOV.UK's tax checker</a>.</li>
          </ul>
        </div>

        <div className="text-center pt-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
          >
            <SafeIcon icon={FiCalculator} />
            Start Calculating
          </Link>
        </div>
      </motion.section>
    </div>
  );
};

export default HowToUse;
